// panelDoorFrame.js
// Standalone SVG renderer for paneeldeuren (panel sliding doors).
// No dependencies, no DOM coupling — returns an SVG string.
//
// Usage (Shopify theme / plain <script type="module">):
//   import { renderDoor } from "{{ 'panelDoorFrame.js' | asset_url }}";
//   container.innerHTML = renderDoor({ count: 3, color: 'black', panelWidthMm: 800, doorHeightMm: 2400 });
//
// Usage (plain <script> — accesses window.PanelDoorFrame):
//   The module self-registers on window when loaded in a browser context.
//
// Usage (Node ESM — import * as PF or import { panelOpenings }):
//   import * as PF from './panelDoorFrame.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * PANEL_FRAME_MM — all values in millimetres.
 *
 * EXACT values come from the product design spec; they must not change.
 * TUNE values are visual estimates — search "// TUNE" to tweak them.
 */
export const PANEL_FRAME_MM = {
  // EXACT: flat outer "rand" (stile) between the door edge and the routed band.
  // Door is 800 mm in the reference; rand = 105 mm.
  stile: 105,
  // EXACT: routed groove band width ("riggeltjes"). Sits at the INNER edge of
  // the stile, between the stile and the raised centre panel.
  routeBand: 48,
  // Full border zone (door edge → raised panel) = stile + routeBand.
  routeInset: 153,
  // EXACT: greep (flush-pull plate) vertical centre, mm from door bottom.
  greepCenterFromBottom: 1050,

  // Top profiel = ONE continuous head-rail across the entire opening, matched
  // to omlijst's topCapH so the rail thickness reads the same across both
  // door types. Doors hang below it.
  topProfielH: 55,
  botProfielH: 12,                 // TUNE: bottom profiel / track height
  // Flush-pull greep — metal plate set into the door face with an open cavity
  // for fingertips. Dimensions match the technical drawings.
  greepPocketH: 170,               // TUNE: flush-pull plate length
  greepPocketW: 35,                // TUNE: flush-pull plate width
  // Greep is centered in the 105 mm stile (≈ (105 − 35) / 2 = 35 mm clear on
  // each side within the rand).
  greepInsetFromEdge: 35,          // TUNE: clearance from the door's vertical edge
  // Routed groove line thickness (each of the 3 concentric grooves inside the
  // 48 mm routeBand). 1.8 mm reads as a soft engraved groove rather than a hard
  // outlined ridge.
  grooveLineW: 1.8,                // TUNE
  // 2-rail overlap: front doors visually overlap back doors at each seam by
  // this band width (in mm), drawn as a soft shadow on the back door.
  seamOverlap: 22,
  // Flat strip between two adjacent vakken's routed bands (Tweevlaks/Drievlaks).
  // Matches the SIDE stile flat width so the visible "border between vakken"
  // reads the same as the side border. Total inter-vak rail visible =
  // routeBand + dividerGap + routeBand = 48 + 105 + 48 = 201 mm.
  dividerGap: 105,                 // TUNE
};

// ---------------------------------------------------------------------------
// Vlakverdeling layout — per pattern, returns the outer vak rects (the area
// where the 3 concentric grooves are drawn FROM). Each vak rect's outer edge
// is the OUTERMOST routed groove; the raised panel inside it is inset by
// routeBand (48 mm). Coordinates are in mm, in a single door's coord system
// (caller offsets by `i * panelWidthMm`).
//
//   Eenvlaks  → 1 vak (full inner area)
//   Tweevlaks → 2 vakken stacked, top 2/3 + bottom 1/3 (with dividerGap)
//   Drievlaks → 3 equal vakken stacked (with 2× dividerGap)
//   Vlak      → no vakken (door face is completely flat, no moulding)
// ---------------------------------------------------------------------------
export function vlakRects(panelWidthMm, doorHeightMm, F, vlak) {
  const W = panelWidthMm, H = doorHeightMm;
  const innerX = F.stile;
  const innerY = F.stile;
  const innerW = W - 2 * F.stile;
  const innerH = H - 2 * F.stile;
  const gap = F.dividerGap;

  switch (vlak) {
    case 'Eenvlaks':
      return [{ x: innerX, y: innerY, w: innerW, h: innerH }];
    case 'Tweevlaks': {
      const avail = innerH - gap;
      const topH = avail * (2 / 3);
      const botH = avail - topH;
      return [
        { x: innerX, y: innerY, w: innerW, h: topH },
        { x: innerX, y: innerY + topH + gap, w: innerW, h: botH },
      ];
    }
    case 'Drievlaks': {
      const avail = innerH - 2 * gap;
      const vH = avail / 3;
      return [
        { x: innerX, y: innerY, w: innerW, h: vH },
        { x: innerX, y: innerY + vH + gap, w: innerW, h: vH },
        { x: innerX, y: innerY + 2 * (vH + gap), w: innerW, h: vH },
      ];
    }
    case 'Vlak':
      return []; // no vakken, no moulding
    case 'Verticaal gelijnd':
    case 'Horizontaal gelijnd':
      // Line-based patterns: no raised vakken; thin parallel grooves are drawn
      // directly on the flat door face in renderDoor (drawLineGrooves).
      return [];
    default:
      // Unknown pattern → render flat (safest fallback during dev)
      return [];
  }
}

// ---------------------------------------------------------------------------
// Door layout helpers — exported pure functions, unit-testable
// ---------------------------------------------------------------------------

/**
 * isFrontDoor — even index = back rail, odd index = front rail.
 * Doors at odd indices (1, 3) are on the front rail and visually overlap
 * their neighbours at the seams.
 */
export function isFrontDoor(i) { return i % 2 === 1; }

// ---------------------------------------------------------------------------
// Vlakverdeling icons — small wireframe SVGs (3 doors, top rail) for the
// vlakverdeling picker. Use `currentColor` so they inherit the picker theme.
// Lookup by vlak id (matches the `custom.vlakverdeling` metaobject values).
// ---------------------------------------------------------------------------
function _vlakIcon(innerFn) {
  // viewBox 60×60 (1:1). Top rail = thin filled band; 2 doors with thin
  // stroked outlines. Inner content drawn by `innerFn(x, y, w, h)`.
  const doors = [8, 31].map((dx) => {
    const inner = innerFn(dx + 2.5, 12, 16, 42); // (x, y, w, h) of door inner area
    return `<rect x="${dx}" y="10" width="21" height="45" fill="none" stroke="currentColor" stroke-width="0.9"/>${inner}`;
  }).join('');
  return `<svg viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect x="6" y="5" width="48" height="4" fill="currentColor" opacity="0.30"/>
    ${doors}
  </svg>`;
}
export const VLAK_ICONS = {
  // 1 raised panel per door
  'Eenvlaks': _vlakIcon((x, y, w, h) =>
    `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="none" stroke="currentColor" stroke-width="0.7"/>`),
  // top vak ⅔ + bottom vak ⅓
  'Tweevlaks': _vlakIcon((x, y, w, h) => {
    const gap = 1.2;
    const topH = (h - gap) * (2 / 3);
    const botH = (h - gap) * (1 / 3);
    return (
      `<rect x="${x}" y="${y}" width="${w}" height="${topH}" fill="none" stroke="currentColor" stroke-width="0.7"/>` +
      `<rect x="${x}" y="${y + topH + gap}" width="${w}" height="${botH}" fill="none" stroke="currentColor" stroke-width="0.7"/>`
    );
  }),
  // 3 equal stacked vakken
  'Drievlaks': _vlakIcon((x, y, w, h) => {
    const gap = 1.2;
    const vH = (h - 2 * gap) / 3;
    return [0, 1, 2].map((i) =>
      `<rect x="${x}" y="${y + i * (vH + gap)}" width="${w}" height="${vH}" fill="none" stroke="currentColor" stroke-width="0.7"/>`
    ).join('');
  }),
  // No vakken — fully flat door
  'Vlak': _vlakIcon(() => ''),
  // Thin vertical lines across the door face
  'Verticaal gelijnd': _vlakIcon((x, y, w, h) => {
    const lines = 7;
    let out = '';
    for (let i = 1; i < lines; i++) {
      const lx = x + (w * i) / lines;
      out += `<line x1="${lx}" y1="${y}" x2="${lx}" y2="${y + h}" stroke="currentColor" stroke-width="0.5" opacity="0.7"/>`;
    }
    return out;
  }),
  // 5 horizontal lines per door — mirror of Verticaal gelijnd, rotated 90°
  'Horizontaal gelijnd': _vlakIcon((x, y, w, h) => {
    const n = 6;
    let out = '';
    for (let i = 1; i < n; i++) {
      const ly = y + (h * i) / n;
      out += `<line x1="${x}" y1="${ly}" x2="${x + w}" y2="${ly}" stroke="currentColor" stroke-width="0.5" opacity="0.7"/>`;
    }
    return out;
  }),
};

/**
 * greepEdges — which vertical edge(s) of door `i` carry a flush-pull greep.
 *
 * Spec (per the configurator drawings, applies to ALL vlakverdelingen):
 *   - Leftmost door  → greep on the LEFT (outer) edge
 *   - Rightmost door → greep on the RIGHT (outer) edge
 *   - All doors in between → greep on BOTH edges
 *
 *   count=2 → 0:[left]                    1:[right]
 *   count=3 → 0:[left] 1:[L,R]            2:[right]
 *   count=4 → 0:[left] 1:[L,R] 2:[L,R]    3:[right]
 *   count=5 → 0:[left] 1:[L,R] 2:[L,R] 3:[L,R] 4:[right]
 *
 * Total greep count = 2 * (count - 1).
 */
export function greepEdges(i, count) {
  if (count <= 1) return ['left'];
  if (i === 0) return ['left'];
  if (i === count - 1) return ['right'];
  return ['left', 'right'];
}

// ---------------------------------------------------------------------------
// Profiel colour presets (mirrors slidingDoorFrame COLOR_PRESETS shape).
// Panel face colour is independent (opts.panelHex).
// ---------------------------------------------------------------------------

// `fill` = plate face (the main profielkleur).
// `deep` = inside-of-cavity tone (slightly darker than `fill` to read as recess,
//   but unmistakably the same colour family — not a different metal).
const PROFIEL_PRESETS = {
  black:       { fill: '#1d1d1d', stroke: '#000',    deep: '#0a0a0a' },
  white:       { fill: '#f5f5f5', stroke: '#cfcfcf', deep: '#f5f5f5' },
  aluminum:    { fill: '#bcbcbf', stroke: '#7a7a7d', deep: '#9ea2a7' },
  kwartsgrijs: { fill: '#8b877f', stroke: '#6c6862', deep: '#5b5751' },
  antraciet:   { fill: '#595d62', stroke: '#3f4247', deep: '#2f3236' },
};

// Default panel face colour when none is provided.
const DEFAULT_PANEL_HEX = '#e8e8e8';

// ---------------------------------------------------------------------------
// Geometry helper
// ---------------------------------------------------------------------------

/**
 * panelOpenings — raised-panel rects (the area inside each vak's routed border),
 * expressed as % of the total SVG. Number of rects per door depends on `vlak`:
 *   Eenvlaks → 1 per door, Tweevlaks → 2, Drievlaks → 3, Vlak → 0.
 *
 * @param {number} count          number of doors
 * @param {number} panelWidthMm   per-door width in mm
 * @param {number} doorHeightMm   door height in mm
 * @param {Object} F              PANEL_FRAME_MM (or compatible)
 * @param {string} [vlak]         vlakverdeling
 * @returns {{ xPct: number, yPct: number, wPct: number, hPct: number }[]}
 */
export function panelOpenings(count, panelWidthMm, doorHeightMm, F, vlak) {
  const totalW = count * panelWidthMm;
  const result = [];
  const rb = F.routeBand;
  const rects = vlakRects(panelWidthMm, doorHeightMm, F, vlak || 'Eenvlaks');
  for (let i = 0; i < count; i++) {
    const ox = i * panelWidthMm;
    rects.forEach((v) => {
      const pX = ox + v.x + rb;
      const pY = v.y + rb;
      const pW = v.w - 2 * rb;
      const pH = v.h - 2 * rb;
      result.push({
        xPct: (pX / totalW) * 100,
        yPct: (pY / doorHeightMm) * 100,
        wPct: (pW / totalW) * 100,
        hPct: (pH / doorHeightMm) * 100,
      });
    });
  }
  return result;
}

// ---------------------------------------------------------------------------
// SVG renderer
// ---------------------------------------------------------------------------

/**
 * renderDoor — render `count` panel sliding doors side by side as an SVG string.
 *
 * @param {Object}  opts
 * @param {2|3|4|5} opts.count            number of panel doors
 * @param {string}  [opts.color='black']  profiel colour preset: 'black'|'white'|'aluminum'
 * @param {number}  opts.panelWidthMm     per-door width in mm
 * @param {number}  opts.doorHeightMm     door height in mm
 * @param {string}  [opts.panelHex]       panel face hex colour; default '#e8e8e8'
 * @param {number}  [opts.pxPerMm=0.15]  render scale (svg width/height attrs in px)
 * @param {string}  [opts.idSuffix='']   unique suffix for SVG <defs> IDs
 * @param {string}  [opts.vlak]          vlakverdeling ('Eenvlaks' is default and current only option)
 * @returns {string} SVG markup
 */
export function renderDoor(opts) {
  const {
    count,
    color = 'black',
    panelWidthMm,
    doorHeightMm,
    panelHex = DEFAULT_PANEL_HEX,
    pxPerMm = 0.15,
    idSuffix = '',
    vlak = 'Eenvlaks',
  } = opts;

  const F = PANEL_FRAME_MM;
  const profiel = typeof color === 'string'
    ? (PROFIEL_PRESETS[color] || PROFIEL_PRESETS.black)
    : color;

  // panelHex reaches us from cart line-item properties (attacker-controllable via
  // /cart/add), and is injected into an SVG fill attribute through innerHTML.
  const safePanelHex = /^#[0-9a-fA-F]{3,8}$/.test(panelHex) ? panelHex : DEFAULT_PANEL_HEX;

  const totalW = count * panelWidthMm;
  const svgW = (totalW * pxPerMm).toFixed(1);
  const svgH = (doorHeightMm * pxPerMm).toFixed(1);

  // --- Build <defs> ---
  // All depth effects use "black-at-opacity fading to transparent" gradients so
  // they read as a darker shade of whatever panelHex is behind them — no hard
  // contrasting colours required.
  const defs = [
    // Routed groove valley shadows — softer than before so the routed border
    // reads as engraved, not hard outlined. Top/left valleys catch more shadow.
    `<linearGradient id="grT_${idSuffix}" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#000" stop-opacity="0.28"/><stop offset="100%" stop-color="#000" stop-opacity="0"/></linearGradient>`,
    `<linearGradient id="grB_${idSuffix}" x1="0" y1="1" x2="0" y2="0"><stop offset="0%" stop-color="#000" stop-opacity="0.14"/><stop offset="100%" stop-color="#000" stop-opacity="0"/></linearGradient>`,
    `<linearGradient id="grL_${idSuffix}" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stop-color="#000" stop-opacity="0.28"/><stop offset="100%" stop-color="#000" stop-opacity="0"/></linearGradient>`,
    `<linearGradient id="grR_${idSuffix}" x1="1" y1="0" x2="0" y2="0"><stop offset="0%" stop-color="#000" stop-opacity="0.17"/><stop offset="100%" stop-color="#000" stop-opacity="0"/></linearGradient>`,
    // Raised-panel bevel: top+left lit, bottom+right shadowed.
    `<linearGradient id="hiT_${idSuffix}" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#fff" stop-opacity="0.34"/><stop offset="100%" stop-color="#fff" stop-opacity="0"/></linearGradient>`,
    `<linearGradient id="hiL_${idSuffix}" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stop-color="#fff" stop-opacity="0.26"/><stop offset="100%" stop-color="#fff" stop-opacity="0"/></linearGradient>`,
    `<linearGradient id="dsT_${idSuffix}" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#000" stop-opacity="0.24"/><stop offset="100%" stop-color="#000" stop-opacity="0"/></linearGradient>`,
    `<linearGradient id="dsB_${idSuffix}" x1="0" y1="1" x2="0" y2="0"><stop offset="0%" stop-color="#000" stop-opacity="0.24"/><stop offset="100%" stop-color="#000" stop-opacity="0"/></linearGradient>`,
    `<linearGradient id="dsL_${idSuffix}" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stop-color="#000" stop-opacity="0.22"/><stop offset="100%" stop-color="#000" stop-opacity="0"/></linearGradient>`,
    `<linearGradient id="dsR_${idSuffix}" x1="1" y1="0" x2="0" y2="0"><stop offset="0%" stop-color="#000" stop-opacity="0.22"/><stop offset="100%" stop-color="#000" stop-opacity="0"/></linearGradient>`,
    // Top profiel cap — simple highlight fading from top to bottom (no sign-change
    // in the middle, which previously read as a visible line through the rail).
    `<linearGradient id="capG_${idSuffix}" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#fff" stop-opacity="0.20"/><stop offset="100%" stop-color="#fff" stop-opacity="0"/></linearGradient>`,
    // Flush-pull cavity inner shadow — VERY subtle so the cavity reads as the
    // profielkleur with just a hint of recess. Stronger shadows turn a white
    // cavity grey, which is the bug we keep hitting.
    `<linearGradient id="cavSh_${idSuffix}" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#000" stop-opacity="0.18"/><stop offset="50%" stop-color="#000" stop-opacity="0.03"/><stop offset="100%" stop-color="#000" stop-opacity="0.08"/></linearGradient>`,
    // 2-rail overlap seam shadow (cast by front door onto back door).
    `<linearGradient id="seamL_${idSuffix}" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stop-color="#000" stop-opacity="0.32"/><stop offset="100%" stop-color="#000" stop-opacity="0"/></linearGradient>`,
    `<linearGradient id="seamR_${idSuffix}" x1="1" y1="0" x2="0" y2="0"><stop offset="0%" stop-color="#000" stop-opacity="0.32"/><stop offset="100%" stop-color="#000" stop-opacity="0"/></linearGradient>`,
  ].join('');

  let svg = `<svg width="${svgW}" height="${svgH}" viewBox="0 0 ${totalW} ${doorHeightMm}" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">`;
  svg += `<defs>${defs}</defs>`;

  // Draws the routed border (3 concentric grooves with valley shadows) and the
  // raised panel inside ONE vak rect. Vak rect coords are in the single-door
  // coord system; caller supplies the door's x-offset (`ox`).
  function drawVak(ox, v) {
    const rb = F.routeBand;
    const hair = F.grooveLineW;       // 1.8 mm — soft engraved groove
    const step = rb / 3;
    const hiRim = 0.7;                // thin highlight rim inside the top + left shadow lines
    for (let g = 0; g < 3; g++) {
      const gIns = g * step;
      const innerW = v.w - 2 * gIns;
      const innerH = v.h - 2 * gIns;
      // Soft shadow lines (4 sides). Top/left catches more shadow (light from
      // top-left); bottom/right is lighter (lit rim).
      svg += `<rect x="${ox+v.x+gIns}" y="${v.y+gIns}" width="${innerW}" height="${hair}" fill="#000" opacity="0.28"/>`;
      svg += `<rect x="${ox+v.x+gIns}" y="${v.y+v.h-gIns-hair}" width="${innerW}" height="${hair}" fill="#000" opacity="0.18"/>`;
      svg += `<rect x="${ox+v.x+gIns}" y="${v.y+gIns}" width="${hair}" height="${innerH}" fill="#000" opacity="0.28"/>`;
      svg += `<rect x="${ox+v.x+v.w-gIns-hair}" y="${v.y+gIns}" width="${hair}" height="${innerH}" fill="#000" opacity="0.18"/>`;
      // Highlight rim just inside the top + left shadow line — adds the soft
      // "engraved into the panel" effect without a hard edge.
      svg += `<rect x="${ox+v.x+gIns}" y="${v.y+gIns+hair}" width="${innerW}" height="${hiRim}" fill="#fff" opacity="0.18"/>`;
      svg += `<rect x="${ox+v.x+gIns+hair}" y="${v.y+gIns}" width="${hiRim}" height="${innerH}" fill="#fff" opacity="0.18"/>`;
      if (g < 2) {
        const vIns = gIns + hair;
        const vW = innerW - 2 * hair;
        const vH = innerH - 2 * hair;
        const vBand = step - 2 * hair;
        svg += `<rect x="${ox+v.x+vIns}" y="${v.y+vIns}" width="${vW}" height="${vBand}" fill="url(#grT_${idSuffix})"/>`;
        svg += `<rect x="${ox+v.x+vIns}" y="${v.y+v.h-vIns-vBand}" width="${vW}" height="${vBand}" fill="url(#grB_${idSuffix})"/>`;
        svg += `<rect x="${ox+v.x+vIns}" y="${v.y+vIns}" width="${vBand}" height="${vH}" fill="url(#grL_${idSuffix})"/>`;
        svg += `<rect x="${ox+v.x+v.w-vIns-vBand}" y="${v.y+vIns}" width="${vBand}" height="${vH}" fill="url(#grR_${idSuffix})"/>`;
      }
    }
    // Raised panel inside this vak
    const pX = ox + v.x + rb, pY = v.y + rb;
    const pW = v.w - 2 * rb, pH = v.h - 2 * rb;
    svg += `<rect x="${pX}" y="${pY}" width="${pW}" height="${pH}" fill="${safePanelHex}"/>`;
    // Dark drop-shadows on ALL FOUR edges of the raised panel. Uses dark gradients
    // (no white) so the edges read the same on light and dark panels — no white
    // streaks on antraciet/zwart. Conveys "raised toward the viewer" via an even
    // shadow outline rather than asymmetric top-left lit / bottom-right shadowed.
    svg += `<rect x="${pX}" y="${pY}" width="${pW}" height="${Math.min(10, pH*0.03)}" fill="url(#dsT_${idSuffix})"/>`;
    svg += `<rect x="${pX}" y="${pY+pH-Math.min(10,pH*0.03)}" width="${pW}" height="${Math.min(10,pH*0.03)}" fill="url(#dsB_${idSuffix})"/>`;
    svg += `<rect x="${pX}" y="${pY}" width="${Math.min(10, pW*0.025)}" height="${pH}" fill="url(#dsL_${idSuffix})"/>`;
    svg += `<rect x="${pX+pW-Math.min(10,pW*0.025)}" y="${pY}" width="${Math.min(10,pW*0.025)}" height="${pH}" fill="url(#dsR_${idSuffix})"/>`;
  }

  // Render one door fully (face → vakken with their routed borders → bottom track
  // → greep). Top rail is drawn ONCE for the whole opening below, not per door.
  function drawDoor(i) {
    const ox = i * panelWidthMm;
    const W  = panelWidthMm;
    const H  = doorHeightMm;

    // 1. door face (starts BELOW the head rail; the toprail covers the top area)
    svg += `<rect x="${ox}" y="${F.topProfielH}" width="${W}" height="${H - F.topProfielH}" fill="${safePanelHex}"/>`;

    // 2. vakken with routed borders + raised panels (skipped for 'Vlak' — no vakken)
    const vRects = vlakRects(W, H, F, vlak);
    vRects.forEach((v) => drawVak(ox, v));

    // 2.5. Line-based vlakverdelingen (Horizontaal gelijnd, Verticaal gelijnd): small routed
    //      grooves drawn directly on the flat door face. Each groove = two thin
    //      parallel dark edge lines with a subtle shadow band between them, so
    //      it reads as a real milled channel (not a flat painted stripe).
    if (vlak === 'Horizontaal gelijnd' || vlak === 'Verticaal gelijnd') {
      const yStart = F.topProfielH;
      const yEnd   = H - F.botProfielH;
      if (vlak === 'Horizontaal gelijnd') {
        // 4 grooves → 5 vakken per door
        const count = 4;
        const step = (yEnd - yStart) / (count + 1);
        for (let g = 1; g <= count; g++) {
          const y = yStart + g * step;
          // top edge of groove (catches more shadow)
          svg += `<rect x="${ox}" y="${y}" width="${W}" height="0.9" fill="#000" opacity="0.48"/>`;
          // groove interior shadow (subtle)
          svg += `<rect x="${ox}" y="${y + 0.9}" width="${W}" height="2.2" fill="#000" opacity="0.10"/>`;
          // bottom edge of groove (lighter — rim catches light from above)
          svg += `<rect x="${ox}" y="${y + 3.1}" width="${W}" height="0.9" fill="#000" opacity="0.32"/>`;
        }
      } else {
        // Verticaal gelijnd — 5 evenly-spaced vertical grooves per door (same
        // groove treatment as Horizontaal gelijnd, just rotated 90°)
        const count = 5;
        const step = W / (count + 1);
        for (let g = 1; g <= count; g++) {
          const x = ox + g * step;
          // left edge of groove (catches more shadow)
          svg += `<rect x="${x}" y="${yStart}" width="0.9" height="${yEnd - yStart}" fill="#000" opacity="0.48"/>`;
          // groove interior shadow (subtle)
          svg += `<rect x="${x + 0.9}" y="${yStart}" width="2.2" height="${yEnd - yStart}" fill="#000" opacity="0.10"/>`;
          // right edge of groove (lighter — rim catches light)
          svg += `<rect x="${x + 3.1}" y="${yStart}" width="0.9" height="${yEnd - yStart}" fill="#000" opacity="0.32"/>`;
        }
      }
    }

    // 3. bottom profiel / track (per-door; doors slide on this)
    svg += `<rect x="${ox}" y="${H-F.botProfielH}" width="${W}" height="${F.botProfielH}" fill="${profiel.deep}" opacity="0.88" stroke="${profiel.stroke}" stroke-width="0.4"/>`;
    svg += `<rect x="${ox}" y="${H-F.botProfielH-1}" width="${W}" height="1" fill="#000" opacity="0.35"/>`;

    // 5. greep(s) — a metal flush-pull plate set into the door face with an open
    //    cavity for fingertips. Whole plate is in profielkleur (so it changes
    //    with Wit/Aluminium/Zwart). Centered in the 105 mm stile.
    const edges = greepEdges(i, count);
    const greepCY = H - F.greepCenterFromBottom;
    const gW = F.greepPocketW, gH = F.greepPocketH;
    edges.forEach(side => {
      const gx = (side === 'left')
        ? ox + F.greepInsetFromEdge
        : ox + W - F.greepInsetFromEdge - gW;
      const gy = greepCY - gH / 2;
      // the metal plate (profielkleur)
      svg += `<rect x="${gx}" y="${gy}" width="${gW}" height="${gH}" rx="2" ry="2" fill="${profiel.fill}" stroke="${profiel.stroke}" stroke-width="0.7"/>`;
      // subtle top highlight on the plate
      svg += `<rect x="${gx+1}" y="${gy+1}" width="${gW-2}" height="1" fill="#fff" opacity="0.28"/>`;
      // the cavity / hole — slightly smaller dark rect cut into the plate
      const cm = 5; // cavity margin
      const cx = gx + cm, cy = gy + cm;
      const cw = gW - 2*cm, ch = gH - 2*cm;
      svg += `<rect x="${cx}" y="${cy}" width="${cw}" height="${ch}" rx="1" ry="1" fill="${profiel.deep}"/>`;
      // inner shadow gradient (darker at the top of the cavity — where light doesn't reach)
      svg += `<rect x="${cx}" y="${cy}" width="${cw}" height="${ch}" rx="1" ry="1" fill="url(#cavSh_${idSuffix})"/>`;
      // top inner edge — where the cavity meets the plate surface
      svg += `<rect x="${cx}" y="${cy}" width="${cw}" height="0.8" fill="#000" opacity="0.55"/>`;
      // bottom inner edge — subtler (light bounces here)
      svg += `<rect x="${cx}" y="${cy+ch-0.8}" width="${cw}" height="0.8" fill="#000" opacity="0.40"/>`;
    });
  }

  // Draw order (so front-rail doors visually overlap back-rail doors at seams):
  //   1. back-rail doors first (even index)
  //   2. seam shadows
  //   3. front-rail doors on top
  //   4. ONE continuous top rail across the entire opening (on top of everything)
  for (let i = 0; i < count; i++) if (!isFrontDoor(i)) drawDoor(i);

  for (let i = 0; i < count - 1; i++) {
    const seamX = (i + 1) * panelWidthMm;
    const h = doorHeightMm - F.botProfielH;
    if (isFrontDoor(i)) {
      svg += `<rect x="${seamX}" y="${F.topProfielH}" width="${F.seamOverlap}" height="${h-F.topProfielH}" fill="url(#seamL_${idSuffix})"/>`;
    } else {
      svg += `<rect x="${seamX-F.seamOverlap}" y="${F.topProfielH}" width="${F.seamOverlap}" height="${h-F.topProfielH}" fill="url(#seamR_${idSuffix})"/>`;
    }
  }

  for (let i = 0; i < count; i++) if (isFrontDoor(i)) drawDoor(i);

  // Continuous head-rail (one piece across the whole opening, doors hang from it)
  svg += `<rect x="0" y="0" width="${totalW}" height="${F.topProfielH}" fill="${profiel.fill}" stroke="${profiel.stroke}" stroke-width="0.4"/>`;
  svg += `<rect x="0" y="0" width="${totalW}" height="${F.topProfielH}" fill="url(#capG_${idSuffix})"/>`;
  svg += `<rect x="0" y="${F.topProfielH}" width="${totalW}" height="2.5" fill="#000" opacity="0.42"/>`;

  svg += `</svg>`;
  return svg;
}

// ---------------------------------------------------------------------------
// Browser global registration (no-op in Node; guarded so SSR-safe)
// ---------------------------------------------------------------------------
if (typeof self !== 'undefined' || typeof window !== 'undefined') {
  const g = (typeof self !== 'undefined' ? self : window);
  const api = { renderDoor, panelOpenings, vlakRects, VLAK_ICONS, PANEL_FRAME_MM };
  g.PanelDoorFrame = api;
  // Register "paneel" as a door-type plugin.
  // Shape matches omlijst: { id, ranges, heightMin, heightMax, optionGroups, renderDoor }
  (g.CfgDoorTypes = g.CfgDoorTypes || {}).paneel = {
    id: 'paneel',
    ranges: { 2: [960, 1960], 3: [1420, 2910], 4: [1880, 3870], 5: [2340, 4830] },
    heightMin: 1800,
    heightMax: 2790,
    optionGroups: [
      'vlakverdeling', 'paneelkleur', 'maten', 'aantal',
      'profielkleur', 'softclose', 'montage',
    ],
    renderDoor,
  };
}
